import { useEffect, useState, useCallback } from "react";
import { api, formatErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { AlertOctagon, CheckCircle2, X } from "lucide-react";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";

function since(iso) {
  const secs = Math.max(0, Math.floor((Date.now() - new Date(iso).getTime()) / 1000));
  if (secs < 60) return `${secs}s`;
  const m = Math.floor(secs / 60);
  if (m < 60) return `${m}m`;
  const h = Math.floor(m / 60);
  const rem = m % 60;
  return `${h}h ${rem}m`;
}

/**
 * Persistent pulsing red banner shown while any network outage is active.
 * Admin sees a "Resolve" action; techs just see the status.
 */
export default function OutageBanner({ role, onChanged }) {
  const { config } = useAuth();
  const [outages, setOutages] = useState([]);
  const [tick, setTick] = useState(0);
  const [resolveTarget, setResolveTarget] = useState(null);
  const [resolveLinked, setResolveLinked] = useState(true);
  const [resolveSms, setResolveSms] = useState(true);
  const [resolveNote, setResolveNote] = useState("");
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/outages", { params: { active_only: true } });
      setOutages(data);
    } catch { /* silent */ }
  }, []);

  useEffect(() => {
    load();
    const id = setInterval(load, 5000);
    const tid = setInterval(() => setTick((t) => t + 1), 30000);
    return () => { clearInterval(id); clearInterval(tid); };
  }, [load]);

  async function submitResolve(e) {
    e.preventDefault();
    if (!resolveTarget) return;
    setSaving(true);
    try {
      const { data } = await api.post(`/outages/${resolveTarget.id}/resolve`, {
        resolve_linked: resolveLinked,
        note: resolveNote || null,
        sms_customers: resolveSms,
      });
      const smsNote = data.sms_resolved_count ? ` · SMS sent to ${data.sms_resolved_count}` : "";
      toast.success(`${resolveTarget.device_name} outage resolved${resolveLinked ? ` · ${resolveTarget.linked_count} ticket(s) closed` : ""}${smsNote}`);
      setResolveTarget(null); setResolveNote(""); setResolveLinked(true); setResolveSms(true);
      load();
      onChanged?.();
    } catch (err) {
      toast.error(formatErr(err.response?.data?.detail));
    } finally { setSaving(false); }
  }

  if (!outages.length) return null;

  return (
    <>
      <div data-testid="outage-banner" className="space-y-2 mb-4">
        {outages.map((o) => (
          <div
            key={o.id}
            className="pulse-danger flex flex-wrap items-center gap-3 rounded-xl border-2 border-rose-500 bg-gradient-to-r from-rose-600/40 via-rose-500/25 to-rose-600/40 px-4 py-3 shadow-lg shadow-rose-500/40"
          >
            <div className="flex items-center gap-2 text-rose-100">
              <AlertOctagon className="w-6 h-6 shrink-0" />
              <span className="font-mono-alt text-[10px] uppercase tracking-widest bg-rose-500 text-white px-2 py-0.5 rounded">HIGH ALERT</span>
            </div>
            <div className="flex-1 min-w-[220px]">
              <div className="text-white font-display font-bold text-sm sm:text-base">
                {o.device_type} <span className="text-rose-200">{o.device_name}</span> DOWN · {o.location}
              </div>
              <div className="text-xs sm:text-sm text-rose-100/90 mt-0.5">
                {o.reason} · <span className="font-mono-alt">down {since(o.started_at)}{tick >= 0 ? "" : ""}</span>
                {o.linked_count ? ` · ${o.linked_count} ticket${o.linked_count === 1 ? "" : "s"} linked` : ""}
              </div>
            </div>
            {role === "admin" && (
              <Button
                data-testid={`resolve-outage-${o.device_name}`}
                onClick={() => { setResolveTarget(o); setResolveNote(""); setResolveLinked(true); }}
                size="sm"
                className="bg-emerald-500 hover:bg-emerald-600 text-white shrink-0"
              >
                <CheckCircle2 className="w-4 h-4 mr-1.5" /> Resolve outage
              </Button>
            )}
          </div>
        ))}
      </div>

      <Dialog open={!!resolveTarget} onOpenChange={(v) => { if (!v) { setResolveTarget(null); setResolveNote(""); setResolveLinked(true); setResolveSms(true); } }}>
        <DialogContent className="bg-slate-950 border-slate-800 text-slate-100 max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CheckCircle2 className="w-5 h-5 text-emerald-400" /> Resolve outage · {resolveTarget?.device_name}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={submitResolve} className="space-y-4">
            <div className="text-xs text-slate-400">
              Device restored at <span className="font-mono-alt text-slate-200">{new Date().toLocaleString()}</span>
              {resolveTarget?.linked_count ? <> · <span className="text-rose-300">{resolveTarget.linked_count} linked ticket(s)</span></> : null}
            </div>
            <div>
              <Label>Restoration note</Label>
              <Input
                data-testid="resolve-outage-note"
                value={resolveNote}
                onChange={(e) => setResolveNote(e.target.value)}
                placeholder={`e.g. Fiber spliced at km 12 · ${resolveTarget?.device_name} online`}
                className="bg-slate-900 border-slate-700 mt-1"
              />
            </div>
            {resolveTarget?.linked_count > 0 && (
              <label className="flex items-start gap-2 text-sm text-slate-300 cursor-pointer">
                <input
                  data-testid="resolve-linked-toggle"
                  type="checkbox"
                  checked={resolveLinked}
                  onChange={(e) => setResolveLinked(e.target.checked)}
                  className="mt-0.5 w-4 h-4 accent-emerald-500"
                />
                <span>
                  Bulk-resolve all <b className="text-emerald-300">{resolveTarget.linked_count}</b> linked ticket(s) with this note.
                  <div className="text-xs text-slate-500 mt-0.5">Uncheck to leave tickets open for individual review.</div>
                </span>
              </label>
            )}
            {resolveTarget?.linked_count > 0 && (
              <label className="flex items-start gap-2 text-sm text-slate-300 cursor-pointer border border-slate-800 rounded-lg px-3 py-2 hover:bg-slate-900/60">
                <input
                  data-testid="resolve-sms-toggle"
                  type="checkbox"
                  checked={resolveSms}
                  onChange={(e) => setResolveSms(e.target.checked)}
                  className="mt-0.5 w-4 h-4 accent-emerald-500"
                />
                <span>
                  SMS every affected customer — <span className="text-slate-400">"Good news — the outage in your area has been restored."</span>
                  <div className="text-xs text-slate-500 mt-0.5">
                    {config?.sms_enabled ? "Twilio live — SMS will be sent to every linked customer." : "SMS is in MOCK mode (add TWILIO_* keys). Message will be logged."}
                  </div>
                </span>
              </label>
            )}
            <div className="flex gap-2 pt-1">
              <Button
                data-testid="confirm-resolve-outage"
                type="submit"
                disabled={saving}
                className="flex-1 bg-emerald-500 hover:bg-emerald-600"
              >
                {saving ? "Resolving…" : "Confirm resolve"}
              </Button>
              <Button
                type="button"
                onClick={() => setResolveTarget(null)}
                variant="outline"
                className="border-slate-700 hover:bg-slate-800"
              >
                <X className="w-4 h-4" />
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </>
  );
}
